import { GridItem, Card } from '@chakra-ui/react';
import {
  IOfferingRes,
  IQuoteOfferingRespSchema,
  SelectedProductsWithinOffering,
  SelectedProductsWithinOfferingForm,
} from '@monetize/types/app';
import { formatInteger } from '@monetize/utils/core';
import {
  MBox,
  MButton,
  MCard,
  MCheckbox,
  MDivider,
  MFlex,
  MGrid,
  MGridItem,
  MSearchInput,
  MSkeleton,
  MStack,
  MText,
} from '../../../../../../components/Monetize';
import { MTooltip } from '../../../../../../components/Monetize/MTooltip';
import { useProductSelection } from '../../../../../../hooks';
import { ManageProductsRow } from './ManageProductsRow';

interface ManageProductsFormProps {
  offering?: IOfferingRes | null;
  quoteOffering?: IQuoteOfferingRespSchema | null;
  updateOfferingItems: (
    offering: SelectedProductsWithinOfferingForm,
  ) => Promise<void>;
  onClose: () => void;
  isSource?: 'modal' | 'drawer';
  isDirty?: boolean;
  setIsProductListChanged?: (val: boolean) => void;
  setProductsLoading: (val: boolean) => void;
  isProductsLoading: boolean;
  disabled?: boolean;
  closeDrawer?: () => void;
  /** Onetime charges cannot be added to an offering carried over from the amended contract */
  disableOneTimeProducts?: boolean;
}

export const ManageProductsForm = ({
  offering,
  quoteOffering,
  updateOfferingItems,
  onClose,
  isSource,
  setIsProductListChanged,
  isProductsLoading,
  setProductsLoading,
  disabled,
  closeDrawer,
  disableOneTimeProducts,
}: ManageProductsFormProps) => {
  const {
    selectedProductCount,
    rootProductValue,
    isAnyProductSelected,
    isAnyChangesMade,
    searchTerm,
    visibleRows,
    isSelectAllDisabled,
    isAllProductSelected,
    isIndeterminate,
    isLoading,
    isSearching,
    isManagedProductsLoading,
    isProductLocked,
    handleCheckboxChange,
    setSearchTerm,
    handleSelectAll,
    onSubmit,
  } = useProductSelection({
    offering: offering!,
    quoteOffering,
    onSave: updateOfferingItems,
    setIsProductListChanged,
    closeDrawer,
    disableOneTimeProducts,
  });

  const isDrawer = isSource === 'drawer';

  const saveButtonToolTipLabel = !isAnyProductSelected
    ? 'At least one product must be selected'
    : undefined;
  return (
    <>
      <MBox px={!isDrawer ? 4 : 0} pt={!isDrawer ? 4 : 0}>
        {!isLoading && (
          <MGrid
            gridTemplateColumns={isDrawer ? '1fr' : '2fr 1fr'}
            rowGap={4}
            w="full"
            alignItems="center"
            pb={4}
          >
            <MGridItem colSpan={2}>
              <MSearchInput
                placeholder="Search Products"
                omitCount
                value={searchTerm}
                onChange={setSearchTerm}
                _placeholder={{
                  color: 'tPurple.lightPurple',
                  fontWeight: '400',
                }}
              />
            </MGridItem>

            <GridItem alignItems={'center'}>
              <MFlex alignItems="center">
                <MCheckbox
                  isDisabled={isSelectAllDisabled || disabled}
                  isChecked={isAllProductSelected}
                  onChange={(value) => handleSelectAll(value.target.checked)}
                  isIndeterminate={isIndeterminate}
                >
                  <MText ml={1}>
                    Select All ({formatInteger(selectedProductCount)}/
                    {formatInteger(rootProductValue.length)})
                  </MText>
                </MCheckbox>
              </MFlex>
            </GridItem>

            <MGridItem colSpan={2}>
              <MDivider />
            </MGridItem>
          </MGrid>
        )}

        {visibleRows.length === 0 && isSearching && (
          <MText fontSize="md" fontWeight="bold" textAlign="center">
            No products found
          </MText>
        )}

        {(isLoading || (!isSearching && visibleRows.length < 1)) && (
          <MStack>
            <MSkeleton h={4} />
            <MSkeleton h={4} />
            <MSkeleton h={4} />
          </MStack>
        )}

        {!isLoading && (
          <MBox
            style={{
              height: isDrawer ? 'calc(100vh - 400px)' : 'calc(80vh - 360px)',
              overflowY: 'auto',
            }}
          >
            <MGrid
              gridTemplateColumns={'1fr'}
              rowGap={4}
              w="full"
              alignItems="center"
            >
              {visibleRows.map(
                (row: SelectedProductsWithinOffering, index: number) => (
                  <ManageProductsRow
                    key={row.id}
                    row={row}
                    index={index}
                    value={row.isSelected}
                    isDrawer={isDrawer}
                    handleCheckboxChange={handleCheckboxChange}
                    disabled={disabled}
                    isLocked={isProductLocked(row)}
                  />
                ),
              )}
            </MGrid>
          </MBox>
        )}

        {isDrawer && (
          <>
            <MDivider />

            <MFlex
              justifyContent="center"
              align="center"
              position="sticky"
              bottom="0"
              background="tWhite.base"
              padding="12px"
              borderTop="1px solid #e2e8f0"
              zIndex="10"
            >
              <MTooltip label={saveButtonToolTipLabel} placement="left">
                <MButton
                  isDisabled={!isAnyChangesMade || !isAnyProductSelected}
                  isLoading={isProductsLoading}
                  onClick={() => {
                    setProductsLoading(true);
                    onSubmit();
                  }}
                >
                  Save
                </MButton>
              </MTooltip>
            </MFlex>
          </>
        )}
      </MBox>
      {!isDrawer && (
        <MStack
          p={4}
          style={{
            borderRadius: '0px 0px 4px 4px',
            background: 'tWhite.base',
            boxShadow: '0px -3px 14px 0px rgba(24, 36, 60, 0.08)',
          }}
          gap={4}
          direction="row"
          align="center"
          justify="end"
          flex={1}
          minH={18}
        >
          <MButton onClick={onClose} variant="cancel" minW="80px">
            Cancel
          </MButton>
          <MTooltip label={saveButtonToolTipLabel}>
            <MButton
              form="product-selection-form"
              type="submit"
              minW="80px"
              size="md"
              onClick={onSubmit}
              isDisabled={
                visibleRows.length === 0 ||
                !isAnyProductSelected ||
                isManagedProductsLoading
              }
            >
              Save
            </MButton>
          </MTooltip>
        </MStack>
      )}
    </>
  );
};

export const ManageProductsFormWithCard = (props: ManageProductsFormProps) => {
  return (
    <MCard variant="borderless" mb="2">
      <Card.Body>
        <ManageProductsForm {...props} />
      </Card.Body>
    </MCard>
  );
};
